import * as React from "react";
import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Button, Col, Modal, Row } from "components/bootstrap";
import FormikInput from "components/common/FormikInput";
import Spinner from "components/common/Spinner";
import { ModalSubmit, IfPermitted } from "components/common";
import { ConfigurationsActions, ConfigurationsStore } from "stores/configurations/ConfigurationsStore";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { getConfig } from "components/configurations/helpers";
import { useStore } from "stores/connect";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import { DEFAULT_PASSWORD_COMPLEXITY_CONFIG, PASSWORD_SPECIAL_CHARACTERS } from "logic/users/passwordComplexity";
const StyledDefList = styled.dl.attrs({ className: "deflist" })(
  ({ theme }) => css`
    &&.deflist {
      dd {
        padding-left: ${theme.spacings.md};
        margin-left: 300px;
      }
    }
  `
);
const LabelSpan = styled.span(
  ({ theme }) => css`
    margin-left: ${theme.spacings.sm};
    font-weight: bold;
  `
);
const configType = ConfigurationType.PASSWORD_COMPLEXITY_CONFIG;
const PasswordComplexityConfig = () => {
  const { t } = useTranslation("configurations");
  const [showModal, setShowModal] = useState(false);
  const [viewConfig, setViewConfig] = useState(void 0);
  const [formConfig, setFormConfig] = useState(void 0);
  const configuration = useStore(ConfigurationsStore, (state) => state == null ? void 0 : state.configuration);
  const sendTelemetry = useSendTelemetry("password-complexity");
  useEffect(() => {
    ConfigurationsActions.listPasswordComplexityConfig(configType).then(() => {
      var _a;
      const config = (_a = getConfig(configType, configuration)) != null ? _a : DEFAULT_PASSWORD_COMPLEXITY_CONFIG;
      setViewConfig(config);
      setFormConfig(config);
    });
  }, [configuration]);
  const saveConfig = (values) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.CONFIGURATIONS.PASSWORD_COMPLEXITY_UPDATED, {
      app_action_value: "configuration-save"
    });
    const payload = { ...values, min_length: Number(values.min_length) };
    ConfigurationsActions.update(configType, payload).then(() => {
      setShowModal(false);
    });
  };
  const resetConfig = () => {
    setShowModal(false);
    setFormConfig(viewConfig);
  };
  const validate = (values) => {
    const errors = {};
    if (!values.min_length || Number(values.min_length) < 1) {
      errors.min_length = t("password_complexity.min_length_error");
    }
    return errors;
  };
  const renderRequirement = (label, value) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("dt", null, label), /* @__PURE__ */ React.createElement("dd", { "aria-label": label }, value));
  const requirementLabel = (required) => required ? t("password_complexity.required") : t("password_complexity.not_required");
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, t("password_complexity.title")), /* @__PURE__ */ React.createElement("p", null, t("password_complexity.description")), !viewConfig ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledDefList, null, renderRequirement(`${t("password_complexity.min_length")}:`, viewConfig.min_length), renderRequirement(
    `${t("password_complexity.require_uppercase")}:`,
    requirementLabel(viewConfig.require_uppercase)
  ), renderRequirement(
    `${t("password_complexity.require_lowercase")}:`,
    requirementLabel(viewConfig.require_lowercase)
  ), renderRequirement(
    `${t("password_complexity.require_numbers")}:`,
    requirementLabel(viewConfig.require_numbers)
  ), renderRequirement(
    `${t("password_complexity.require_special_chars")}:`,
    requirementLabel(viewConfig.require_special_chars)
  )), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      type: "button",
      bsSize: "xs",
      bsStyle: "info",
      onClick: () => {
        setShowModal(true);
      }
    },
    t("shared.edit_configuration")
  )), /* @__PURE__ */ React.createElement(Modal, { show: showModal && !!formConfig, onHide: resetConfig }, /* @__PURE__ */ React.createElement(Formik, { onSubmit: saveConfig, initialValues: formConfig, validate }, ({ isSubmitting }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, t("password_complexity.update_title"))), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "min_length",
      id: "min_length",
      label: t("password_complexity.min_length"),
      min: 1,
      required: true,
      help: t("password_complexity.min_length_help")
    }
  )), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "require_uppercase",
      id: "require_uppercase",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("password_complexity.require_uppercase")),
      help: t("password_complexity.require_uppercase_help")
    }
  )), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "require_lowercase",
      id: "require_lowercase",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("password_complexity.require_lowercase")),
      help: t("password_complexity.require_lowercase_help")
    }
  )), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "require_numbers",
      id: "require_numbers",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("password_complexity.require_numbers")),
      help: t("password_complexity.require_numbers_help")
    }
  )), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "require_special_chars",
      id: "require_special_chars",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("password_complexity.require_special_chars")),
      help: /* @__PURE__ */ React.createElement(React.Fragment, null, t("password_complexity.require_special_chars_help"), /* @__PURE__ */ React.createElement("br", null), PASSWORD_SPECIAL_CHARACTERS)
    }
  )))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onCancel: resetConfig,
      isSubmitting,
      isAsyncSubmit: true,
      submitLoadingText: t("shared.update_configuration"),
      submitButtonText: t("shared.update_configuration")
    }
  )))))));
};
export default PasswordComplexityConfig;
